import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { ProcessingDetailsPanel } from '../components/ProcessingDetailsPanel';
import { TransactionFilters } from '../components/TransactionFilters';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { AssignmentAPI, TransactionAPI } from '../services/api';
import { UnifiedTransaction } from '../types/payment';
import { useTransactionFilters } from '../hooks/useTransactionFilters';
import './MyAssignedProcessing.css';

interface ProcessingSummary {
  total_assigned: number;
  assigned_today: number;
  in_process: number;
  completed: number;
}

const MyAssignedProcessing: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<UnifiedTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProcessingSummary>({
    total_assigned: 0,
    assigned_today: 0,
    in_process: 0,
    completed: 0
  });
  const [sortConfig, setSortConfig] = useState<{
    field: keyof UnifiedTransaction;
    direction: 'asc' | 'desc';
  }>({
    field: 'assigned_at',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Initialize filters
  const {
    filters,
    filteredTransactions,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filterStats
  } = useTransactionFilters(transactions);

  useEffect(() => {
    if (user?.role === 'processor_agent' || user?.role === 'processor_team_lead') {
      loadAssignedSales();
    }
  }, [user?.role]);

  const loadAssignedSales = async () => {
    try {
      setLoading(true);
      // Table page + true summary tiles (server aggregate over the full dataset).
      // The previous client-side calc summed only the fetched page, and
      // "completed today" relied on a field the API doesn't return (always 0).
      const [assignedSales, assignedStats] = await Promise.all([
        AssignmentAPI.getMyAssignedSales(0, 2000),
        AssignmentAPI.getMyAssignedStats(),
      ]);
      setTransactions(assignedSales);
      setSummary(assignedStats);

      setLoading(false);
    } catch (error: any) {
      console.error('Failed to load assigned sales:', error);
      AlertService.error('Failed to load assigned processing tasks');
      setLoading(false);
    }
  };

  const handleTransactionClick = (transaction: UnifiedTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsPanel(true);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptData = async (transactionId: string, reason: string) => {
    try {
      const response = await TransactionAPI.decryptTransactionData(transactionId, reason);
      setDecryptedData(response);
      setShowEncryptedData(transactionId);
      AlertService.success('Transaction data decrypted successfully');
    } catch (error: any) {
      console.error('Decryption failed:', error);
      AlertService.error(error.message || 'Failed to decrypt data');
    }
  };

  const handleStatusUpdate = async (transactionId: string, newStatus: string) => {
    try {
      setUpdatingStatus(transactionId);
      await AssignmentAPI.updateProcessingStatus(transactionId, newStatus);
      AlertService.success(`Status updated to ${newStatus}`);
      // Reload transactions to get updated data
      await loadAssignedSales();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getValidStatusTransitions = (currentStatus: string) => {
    // All possible processing statuses
    const allStatuses = ['ASSIGNED', 'IN_PROCESS', 'COMPLETED', 'CANCELLED', 'DOCUMENTS_REQUIRED', 'CHARGEBACK'];
    
    // For admins, directors, and team leads - allow any status transition
    if (user?.role && ['director', 'admin', 'processor_team_lead', 'floor_manager'].includes(user.role)) {
      return allStatuses.filter(status => status !== currentStatus);
    }
    
    // For regular processor agents - restricted transitions
    const transitions: Record<string, string[]> = {
      'ASSIGNED': ['IN_PROCESS', 'CANCELLED', 'DOCUMENTS_REQUIRED'],
      'IN_PROCESS': ['COMPLETED', 'CANCELLED', 'DOCUMENTS_REQUIRED', 'CHARGEBACK'],
      'DOCUMENTS_REQUIRED': ['IN_PROCESS', 'CANCELLED'],
      'COMPLETED': ['CHARGEBACK'],
      'CANCELLED': [],
      'CHARGEBACK': []
    };
    return transitions[currentStatus] || [];
  };

  const handleSort = (field: keyof UnifiedTransaction) => {
    setSortConfig(prevConfig => {
      let newDirection: 'asc' | 'desc';
      
      if (prevConfig.field === field) {
        newDirection = prevConfig.direction === 'asc' ? 'desc' : 'asc';
      } else {
        newDirection = field === 'assigned_at' ? 'desc' : 'asc';
      }
      
      return {
        field,
        direction: newDirection
      };
    });
  };


  // Optimized sorting and pagination
  const sortedAndPaginatedTransactions = React.useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return [];
    }

    // Sort transactions
    const toSort = [...filteredTransactions];
    toSort.sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];
      
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      else if (aVal > bVal) comparison = 1;
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return toSort.slice(startIndex, endIndex);
  }, [filteredTransactions, sortConfig, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  if (!user || (user.role !== 'processor_agent' && user.role !== 'processor_team_lead')) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-warning" role="alert">
          You need to be a processor agent to view this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen isVisible={true} message="Loading assigned tasks..." />;
  }

  return (
    <DashboardLayout>
      <div className="my-assigned-processing">
        <div className="page-header">
          <div>
            <h1>My Processing Tasks</h1>
            <p>Sales and transactions assigned to you for processing</p>
          </div>
        </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        updateFilter={updateFilter}
        clearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        transactions={transactions}
        filterStats={filterStats}
        showProcessingStatusFilter={true}
        showSaleFilters={true}
      />

        {/* Summary Statistics */}
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-number">{summary.total_assigned}</div>
            <div className="summary-label">Total Assigned</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{summary.assigned_today}</div>
            <div className="summary-label">Assigned Today</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{summary.in_process}</div>
            <div className="summary-label">In Process</div>
          </div>
          <div className="summary-card">
            <div className="summary-number">{summary.completed}</div>
            <div className="summary-label">Completed</div>
          </div>
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="filter-summary">
            <span>Showing {filterStats.filtered} of {filterStats.total} transactions</span>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All Filters
            </button>
          </div>
        )}

        {/* Transactions Table */}
        <div className="transactions-section">
          <div className="section-header">
            <h2>My Processing Tasks ({filteredTransactions.length})</h2>
            <button className="btn btn-outline-primary" onClick={loadAssignedSales} disabled={loading}>
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('created_by_name')}>
                    Created By 
                    {sortConfig.field === 'created_by_name' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('first_name')}>
                    Customer
                    {sortConfig.field === 'first_name' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('amount')}>
                    Amount
                    {sortConfig.field === 'amount' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>
                    Sale Date
                    {sortConfig.field === 'created_at' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Category</th>
                  <th>State</th>
                  <th className="sortable" onClick={() => handleSort('processing_status')}>
                    Status
                    {sortConfig.field === 'processing_status' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Assigned By</th>
                  <th className="sortable" onClick={() => handleSort('assigned_at')}>
                    Assigned At
                    {sortConfig.field === 'assigned_at' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndPaginatedTransactions.map((transaction) => {
                  const currentStatus = transaction.processing_status || 'PENDING_ASSIGNMENT';
                  const customerName = `${transaction.first_name || ''} ${transaction.last_name || ''}`.trim() || 'Unknown';
                  
                  return (
                    <tr key={transaction.id}>
                      <td>{(transaction as any).created_by_name || 'Unknown'}</td>
                      <td>{customerName}</td>
                      <td>${transaction.amount?.toFixed(2) || '0.00'}</td>
                      <td>
                        {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() + ' ' + new Date(transaction.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                      </td>
                      <td>{transaction.category || 'N/A'}</td>
                      <td>
                        {transaction.sale_state || 'N/A'}
                        {transaction.three_ds_status === 'Y' && (
                          <span className="badge-3ds">3DS</span>
                        )}
                        {transaction.three_ds_status === 'Y-3RI' && (
                          <span className="badge-3ds">3RI</span>
                        )}
                      </td>
                      <td>
                        {/* Dynamic status dropdown for processor agents */}
                        <select
                          value={currentStatus}
                          onChange={(e) => handleStatusUpdate(transaction.id, e.target.value)}
                          disabled={updatingStatus === transaction.id}
                          className="status-dropdown"
                        >
                          <option value={currentStatus}>{currentStatus}</option>
                          {getValidStatusTransitions(currentStatus).map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                        {updatingStatus === transaction.id && (
                          <span className="updating-status">
                            Updating...
                          </span>
                        )}
                      </td>
                      <td>
                        {(transaction as any).assigned_by_name || 'Unknown'}
                      </td>
                      <td>
                        {(transaction as any).assigned_at ? new Date((transaction as any).assigned_at).toLocaleDateString() + ' ' + new Date((transaction as any).assigned_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleTransactionClick(transaction)}
                            title="View Details"
                          >
                            📋 Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <p>
                  {transactions.length === 0 
                    ? "No processing tasks assigned to you yet." 
                    : "No transactions match the current filters."
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={filteredTransactions.length}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>

      {/* Transaction Details Panel */}
      <ProcessingDetailsPanel
        transaction={selectedTransaction}
        isOpen={showDetailsPanel}
        onClose={handleCloseDetails}
        decryptedData={showEncryptedData === ((selectedTransaction as any)?.transaction_id || selectedTransaction?.id) ? decryptedData : null}
        onDecryptData={handleDecryptData}
      />

      </div>
    </DashboardLayout>
  );
};

export default MyAssignedProcessing;